import { generateScaleFingerings, fingeringToNeckPositions } from "../fingeringEngine";
import {
  fingeringToPlaybackSequence,
  selectCanonical3NpsFingerings,
  NPS_POSITION_LABELS,
} from "../explore3nps";
import type { NeckPosition, NoteName } from "../guitarEngine";
import type { ScalePathMission } from "./scalePathPlan";

export interface ScalePathMissionRuntime {
  sequence: NeckPosition[];
  patternNotes: NeckPosition[];
  positionLabel: string;
  positionIndex: number;
}

/**
 * Secuencia pedagógica: POS canónica + alcance octave/extend + tope de traste.
 */
export function buildScalePathMissionRuntime(
  mission: ScalePathMission
): ScalePathMissionRuntime {
  const fingerings = generateScaleFingerings(mission.root, mission.scaleType, 15);
  const canonical = selectCanonical3NpsFingerings(fingerings);
  const idx = Math.min(mission.positionIndex, Math.max(0, canonical.length - 1));
  const fp = canonical[idx];

  if (!fp) {
    return {
      sequence: [],
      patternNotes: [],
      positionLabel: NPS_POSITION_LABELS[0] ?? "POS I",
      positionIndex: 0,
    };
  }

  let sequence = fingeringToPlaybackSequence(fp, mission.root, mission.playbackRange);
  sequence = sequence.filter((p) => p.fret <= mission.maxFret);

  if (mission.direction === "both" && sequence.length > 1) {
    const desc = [...sequence.slice(0, -1)].reverse();
    sequence = [...sequence, ...desc];
  }

  const patternNotes = fingeringToNeckPositions(fp)
    .filter((p) => p.fret <= mission.maxFret)
    .map((p) => ({
      ...p,
      highlight:
        p.degree === "R" || p.note === mission.root
          ? ("root" as const)
          : ("scale" as const),
    }));

  return {
    sequence,
    patternNotes,
    positionLabel: NPS_POSITION_LABELS[idx] ?? `POS ${idx + 1}`,
    positionIndex: idx,
  };
}

export function pitchClassMatches(target: NoteName, detected: NoteName): boolean {
  const pc = (n: NoteName) => {
    const map: Record<string, number> = {
      C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6,
      G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
    };
    return map[n] ?? 0;
  };
  return pc(target) === pc(detected);
}
